import type * as React from 'react'
import type { TenantProfileDto } from '@lib/dto/tenant-settings.dto'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { API_BASE_URL } from '@/helpers/apiClient'
import { CORNER_LABELS, FONT_LABELS } from './brand-look'

interface SummaryRowProps {
  label: string
  children: React.ReactNode
}

function SummaryRow({ label, children }: SummaryRowProps) {
  return (
    <div className="grid grid-cols-3 gap-3 py-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="col-span-2 min-w-0 break-words">{children}</dd>
    </div>
  )
}

/**
 * The stored copy, loaded from the API (the app's CSP allows no third-party
 * images). A logo URL without a copy couldn't be fetched when it was saved.
 */
function renderLogo(profile: Pick<TenantProfileDto, 'displayName' | 'logoUrl' | 'logoSrc'>) {
  if (profile.logoSrc) {
    return <img src={`${API_BASE_URL}${profile.logoSrc}`} alt={`${profile.displayName} logo`} className="max-h-10 max-w-48 object-contain" />
  }
  if (profile.logoUrl) {
    return <span className="text-muted-foreground">Set, but PixelSign couldn't fetch it yet. Save it again to retry.</span>
  }
  return <span className="text-muted-foreground">None</span>
}

interface BrandingSummaryProps {
  profile: TenantProfileDto
  description?: React.ReactNode
}

/** Sender identity and branding at a glance. The swatch and logo are the tenant's data, styled inline. */
export function BrandingSummary({ profile, description }: BrandingSummaryProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>What signers see</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border text-body">
          <SummaryRow label="Display name">{profile.displayName}</SummaryRow>
          <SummaryRow label="Email sender">
            {profile.fromName} · reply-to {profile.replyTo}
          </SummaryRow>
          <SummaryRow label="Accent color">
            {profile.accentColor ? (
              <span className="inline-flex items-center gap-2">
                <span
                  className="size-4 rounded-sm border border-border-strong"
                  style={{ backgroundColor: profile.accentColor }}
                />
                <span className="font-mono text-caption">{profile.accentColor}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">PixelSign default</span>
            )}
          </SummaryRow>
          <SummaryRow label="Corners">
            {CORNER_LABELS[profile.cornerRadius]}
            {profile.cornerRadius === 'medium' ? <span className="text-muted-foreground"> (PixelSign default)</span> : null}
          </SummaryRow>
          <SummaryRow label="Font">
            {FONT_LABELS[profile.fontFamily]}
            {profile.fontFamily === 'inter' ? <span className="text-muted-foreground"> (PixelSign default)</span> : null}
          </SummaryRow>
          <SummaryRow label="Logo">
            {renderLogo(profile)}
          </SummaryRow>
          <SummaryRow label="Time zone">{profile.timezone}</SummaryRow>
        </dl>
      </CardContent>
    </Card>
  )
}
